import { createContext, type RefObject } from 'react';

import type { ViewCommand, ViewCommandApi } from '../types/command';

/**
 * React context carrying the palette API for the currently-mounted
 * view command. Populated only when the palette is in view mode;
 * consumed indirectly by the view's `Component` via the command's
 * own typed `useView()` method (which casts this value to the
 * command's `TPrefill` shape).
 *
 * The context carries `unknown` at the type level because a single
 * React context can't be type-parametric over which view is
 * currently mounted. The per-command `useView()` factory is where
 * the prefill type gets re-attached at the consumer boundary; that
 * cast is the only place in the codebase that needs to admit we
 * can't typecheck this end-to-end. Tradeoff worth taking for clean
 * author ergonomics (no generics at the consumer site).
 *
 * The context value is stable per view-entry: a fresh API object is
 * memoised the first time the view renders and reused on every
 * subsequent render until the view exits. Consumers that destructure
 * `{ close, back, prefill }` into their own closures don't have to
 * worry about stale references.
 */
export const CommandPaletteViewContext = createContext<ViewCommandApi<unknown> | null>(null);

/**
 * Internal chrome shared by the compound view components. Unlike the
 * public view API above, this is concerned only with rendering the
 * current command and wiring its committed primary-action button to the
 * shell's keyboard handler.
 */
export interface CommandPaletteViewRuntime {
  command: ViewCommand<unknown>;
  onBack: () => void;
  primaryActionRef: RefObject<HTMLButtonElement | null>;
}

export const CommandPaletteViewRuntimeContext = createContext<CommandPaletteViewRuntime | null>(null);
